import { memo } from "react";

import { Switch, Typography } from "antd";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DATA_RANGE_LABELS } from "entities/charts/eva/translators/violinDataProcessing";
import { DataRange } from "types";

import { useChartFeatureHandlers } from "hooks";
import { useChartEntities } from "hooks/charts";

import { Tooltip } from "components/base";
import SegmentedToggle from "components/shared/SegmentedToggle";

import { ALL_PRODUCT_TYPES } from "../../../../constants";
import {
  Container,
  RightSwitch,
  StyledDivider,
  StyledIcon,
  StyledInput,
  StyledSpace
} from "../shared/StyledComponents";

const { Text } = Typography;

const DATA_RANGE_OPTIONS = Object.entries(DATA_RANGE_LABELS).map(([value, label]) => ({
  label,
  value
}));

const SettingsOptions = () => {
  const { id } = useChartsContext();
  const { handleNumericChange, handleToggle, updateChartPath } =
    useChartFeatureHandlers();
  const {
    cutoff,
    shutInMonths,
    movingAverageDays,
    survivorBias,
    survivorBiasAsForecast,
    useWeightedRatioAverage,
    lockUnits,
    pressureChartSettings,
    product,
    useLineOfBestFit,
    showZeros,
    dataRangeSelect,
    chartType
  } = useChartEntities(id);

  const isProductivityIndexChecked = pressureChartSettings?.active;
  const isPressureChart =
    product === ALL_PRODUCT_TYPES.CasingPressure.label ||
    product === ALL_PRODUCT_TYPES.TubingPressure.label;
  const chartSupportsProductivityIndex = chartType !== EvaChart.RateDate;
  const lineOfBestFitEnabled = useLineOfBestFit?.active;

  const updateProductivityIndex = (active: boolean) => {
    updateChartPath("features.pressureChartSettings.active", active);
  };

  return (
    <StyledSpace direction="vertical" split={<StyledDivider />}>
      <Container>
        <Text>Cut Off (%)</Text>
        <Tooltip title="Calculate average until well count below cutoff percent">
          <StyledIcon />
        </Tooltip>
        <StyledInput
          type="number"
          value={cutoff}
          min={0}
          max={100}
          controls={false}
          data-testid="cutOffInput"
          onChange={(val: string | null | number) => {
            if (val === null || val === "") {
              return;
            }
            handleNumericChange("cutoff")(val);
          }}
        />
      </Container>
      <Container>
        <Text>Shut In Months</Text>
        <Tooltip title="Number of non producing months to be considered shut-in. Shut-in months are removed from historical production.">
          <StyledIcon />
        </Tooltip>
        <StyledInput
          type="number"
          value={shutInMonths}
          min={0}
          max={20}
          controls={false}
          data-testid="shutInMonthsInput"
          onChange={(val: string | null | number) => {
            if (val === null || val === "") {
              return;
            }
            val = Math.round(Number(val));
            handleNumericChange("shutInMonths")(val);
          }}
        />
      </Container>

      <Container>
        <Text>Moving Average Days</Text>
        <Tooltip title="The number of days to use to calculate the simple moving average.">
          <StyledIcon />
        </Tooltip>
        <StyledInput
          type="number"
          value={movingAverageDays}
          min={0}
          controls={false}
          data-testid="movingAverageDayInput"
          onChange={(val: string | null | number) => {
            if (val === null || val === "") {
              return;
            }
            handleNumericChange("movingAverageDays")(Math.round(Number(val)));
          }}
        />
      </Container>

      <Container>
        <Text>Prevent Survivor Bias</Text>
        <Tooltip title="Allow wells that complete early to continue to affect average">
          <StyledIcon />
        </Tooltip>
        <Switch
          data-testid="survivorBiasCB"
          size="small"
          checked={survivorBias}
          onChange={handleToggle("survivorBias")}
        />
      </Container>

      {survivorBiasAsForecast?.show && (
        <Container>
          <Text>Count Survivor Bias Wells as Forecasted</Text>
          <Tooltip title="Wells kept at 0 by Prevent Survivor Bias count only in Total Wells by default. Turn on to count them as Forecasted Wells in Chart Tooltip">
            <StyledIcon />
          </Tooltip>
          <Switch
            data-testid="survivorBiasAsForecastCB"
            size="small"
            checked={survivorBiasAsForecast?.active}
            disabled={!survivorBias || survivorBiasAsForecast?.enabled === false}
            onChange={handleToggle("survivorBiasAsForecast")}
          />
        </Container>
      )}

      <Container>
        <Text>Weighted Ratio</Text>
        <Tooltip title="Cumulative numerator/cumulative denominator">
          <StyledIcon />
        </Tooltip>
        <Switch
          data-testid="weightedRatioCB"
          size="small"
          checked={useWeightedRatioAverage}
          onChange={handleToggle("useWeightedRatioAverage")}
        />
      </Container>

      <Container>
        <Text>Lock Rate Units</Text>
        <Tooltip title="Locking rate units will keep the units on the y-axis constant, rather than switching between bbl/d and Mbbl/d, for example">
          <StyledIcon />
        </Tooltip>
        <Switch
          data-testid="lockUnits"
          size="small"
          checked={lockUnits}
          onChange={handleToggle("lockUnits")}
        />
      </Container>

      {useLineOfBestFit?.show && (
        <Container>
          <Text>Show Lines of Best Fit</Text>
          <RightSwitch
            data-testid="linesOfBestFit"
            size="small"
            checked={lineOfBestFitEnabled}
            onChange={handleToggle("useLineOfBestFit")}
          />
        </Container>
      )}

      {showZeros?.show && (
        <Container>
          <Text>Show Zeros</Text>
          <Tooltip title="Log charts can't plot zero — zeros are plotted as a very small value.">
            <StyledIcon />
          </Tooltip>
          <RightSwitch
            data-testid="showZeros"
            size="small"
            checked={showZeros?.active}
            onChange={handleToggle("showZeros")}
          />
        </Container>
      )}

      {dataRangeSelect?.show && (
        <Container>
          <Text>Data Range</Text>
          <Tooltip title="Limit each group to values inside the selected percentile range before computing the box, jitter, and violin views.">
            <StyledIcon />
          </Tooltip>
          <SegmentedToggle
            size="small"
            data-testid="dataRangeSelect"
            options={DATA_RANGE_OPTIONS}
            value={(dataRangeSelect?.properties?.range ?? "all") as DataRange}
            onChange={(value) =>
              updateChartPath("features.dataRangeSelect.properties.range", value)
            }
          />
        </Container>
      )}

      {isPressureChart && chartSupportsProductivityIndex && (
        <Container>
          <Text>Productivity Index</Text>
          <Tooltip title="Plot the productivity index instead of the selected pressure.">
            <StyledIcon />
          </Tooltip>
          <Switch
            data-testId="productivityIndexSwitch"
            size="small"
            checked={isProductivityIndexChecked}
            onChange={updateProductivityIndex}
          />
        </Container>
      )}
    </StyledSpace>
  );
};

export default memo(SettingsOptions);
